import React, { useEffect, useRef, useState } from "react";
import { Input, Spin, Tag } from "antd";
import { ChevronDown } from "react-feather";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { League } from "./futuresTransforms";

interface FuturesLeagueSearchProps {
  selected: League | null;
  options: League[];
  isLoading: boolean;
  isError: boolean;
  onSelect: (league: League) => void;
  onSearchChange: (term: string) => void;
}

function FuturesLeagueSearch({
  selected,
  options,
  isLoading,
  isError,
  onSelect,
  onSearchChange,
}: FuturesLeagueSearchProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(selected?.name ?? "");
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the closed field in sync when the parent changes the selection
  // (e.g. sport switch auto-picks the first default league).
  useEffect(() => {
    if (!open) {
      setInputValue(selected?.name ?? "");
    }
  }, [selected, open]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  const clearBlurTimer = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  };

  const closeAndRestore = () => {
    setOpen(false);
    setInputValue(selected?.name ?? "");
    onSearchChange("");
  };

  const handleFocus = () => {
    clearBlurTimer();
    setOpen(true);
  };

  const handleChange = (value: string) => {
    setInputValue(value);
    setOpen(true);
    onSearchChange(value);
  };

  const handleSelect = (league: League) => {
    clearBlurTimer();
    onSelect(league);
    setInputValue(league.name);
    setOpen(false);
    onSearchChange("");
  };

  const handleBlur = () => {
    // Delay so a mousedown/click on a result can fire before we close.
    clearBlurTimer();
    blurTimerRef.current = setTimeout(() => {
      closeAndRestore();
    }, 150);
  };

  return (
    <div
      className="futures-filter-field futures-league-search"
      data-testid="futures-league-search"
    >
      <label className="futures-filter-label" htmlFor="futuresLeagueSearch">
        {AppStrings.LEAGUE}
      </label>
      <div className="futures-league-search-combobox autocomplete">
        <div className="futures-league-search-input-wrap">
          <Input
            id="futuresLeagueSearch"
            data-testid="futures-league-search-input"
            placeholder={AppStrings.SEARCH_LEAGUES}
            value={inputValue}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autoComplete="off"
            aria-expanded={open}
            aria-controls="futures-league-search-results"
            aria-autocomplete="list"
          />
          <ChevronDown
            size={16}
            className={`futures-league-search-chevron ${open ? "open" : ""}`}
            aria-hidden
          />
        </div>

        {open && (
          <div
            className="results futures-league-search-results"
            id="futures-league-search-results"
            role="listbox"
            data-testid="futures-league-search-results"
          >
            {isLoading ? (
              <div
                className="empty-mini"
                data-testid="futures-league-search-loading"
              >
                <Spin size="small" />
              </div>
            ) : isError ? (
              <div
                className="empty-mini"
                data-testid="futures-league-search-error"
              >
                {AppStrings.FAILED_TO_LOAD_LEAGUES}
              </div>
            ) : options.length === 0 ? (
              <div
                className="empty-mini"
                data-testid="futures-league-search-empty"
              >
                {inputValue.trim()
                  ? AppStrings.NO_RESULTS_FOUND
                  : AppStrings.NO_LEAGUES_CONFIGURED}
              </div>
            ) : (
              options.map((league) => {
                const isSelected = selected?.id === league.id;
                return (
                  <div
                    key={league.id}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={-1}
                    data-testid={`futures-league-option-${league.id}`}
                    className={`result-item ${isSelected ? "selected" : ""}`}
                    onMouseDown={(e) => {
                      // Prevent input blur from racing the click.
                      e.preventDefault();
                      handleSelect(league);
                    }}
                  >
                    <div className="result-main">
                      <span className="result-name">{league.name}</span>
                      {league.country_name ? (
                        <Tag className="result-tag result-country-tag">
                          {league.country_name}
                        </Tag>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FuturesLeagueSearch;
